// src/pages/partners/partners-logged-in/routes/client/exercise.tsx
import React from "react";
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { Theme, alpha } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import TimerIcon from "@mui/icons-material/Timer";
import ExerciseDashboardWeb from "./ExerciseDashboardWeb";
import TemplateScroller from "./template";
import { exercisesList } from "./exercise-list";
import { TemplateType, User, Workout } from "../../../../models/types";

// Auth
import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import GlassContainer from "../../components/liquid-glass";

// 🆕 from Client
export type DateRange = { start: Date; end: Date };

type ExerciseListProps = {
  workouts: Workout[];
  isMetricUnits: boolean;
  client: User;
  /** Unified date range coming from Client header */
  dateRange: DateRange;
};

const formatDateLong = (d: Date) =>
  d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const poolData = {
  UserPoolId: "us-east-2_nECr2VdPc",
  ClientId: "6jd4v12t5keoj2eupcrjkcia2c",
};
const userPool = new CognitoUserPool(poolData);

// Your Lambda API URL
const LAMBDA_BASE =
  "https://m01op8z79h.execute-api.us-east-2.amazonaws.com/default/avro-users";

// --------- ID helpers ----------
const genId = () =>
  globalThis.crypto?.randomUUID?.() ??
  `id_${Date.now()}_${Math.random().toString(36).slice(2)}`;

// Ensure ids exist for template + nested items
function ensureTemplateIds<T extends TemplateType>(template: T): T {
  const withId: any = { ...template };
  if (!withId.id) withId.id = genId();

  if (Array.isArray(withId.exercises)) {
    withId.exercises = withId.exercises.map((et: any) => {
      const copy = { ...et };
      if (!copy.id) copy.id = genId();

      if (Array.isArray(copy.sets)) {
        copy.sets = copy.sets.map((s: any) => {
          const sCopy = { ...s };
          if (!sCopy.id) sCopy.id = genId();
          return sCopy;
        });
      }

      if (copy.restTimer && !copy.restTimer.id) {
        copy.restTimer = { ...copy.restTimer, id: genId() };
      }
      return copy;
    });
  }

  return withId;
}

// Merge or insert template by id
function upsertTemplate(templates: TemplateType[], t: TemplateType) {
  const idx = templates.findIndex((x) => x.id === t.id);
  if (idx === -1) return [...templates, t];
  const next = templates.slice();
  next[idx] = t;
  return next;
}

const ExerciseList: React.FC<ExerciseListProps> = ({
  workouts,
  isMetricUnits,
  client,
  dateRange,
}) => {
  const [selected, setSelected] = React.useState<Workout | null>(null);
  const isLg = useMediaQuery((t: Theme) => t.breakpoints.up("lg"));

  // --- Auth token (fetch like PartnersLoggedIn) ---
  const [idToken, setIdToken] = React.useState<string>("");
  const [isFetchingToken, setIsFetchingToken] = React.useState<boolean>(true);

  React.useEffect(() => {
    setIsFetchingToken(true);
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      setIsFetchingToken(false);
      return;
    }
    currentUser.getSession((err: any, session: CognitoUserSession | null) => {
      if (err) {
        console.error("ExerciseList: error retrieving session:", err);
        setIsFetchingToken(false);
        return;
      }
      if (session && session.isValid()) {
        setIdToken(session.getIdToken().getJwtToken());
      }
      setIsFetchingToken(false);
    });
  }, []);

  // Filter workouts by the unified range
  const filtered = React.useMemo(() => {
    if (!workouts || workouts.length === 0) return [];
    const s = dateRange.start.getTime();
    const e = dateRange.end.getTime();
    return workouts
      .filter((w) => {
        const t = new Date(w.dayOfExecution).getTime();
        return t >= s && t <= e;
      })
      .sort(
        (a, b) =>
          new Date(b.dayOfExecution).getTime() -
          new Date(a.dayOfExecution).getTime()
      );
  }, [workouts, dateRange]);

  const exercises = selected?.template?.exercises ?? [];

  // Resolve a userId for lambda
  const userId = React.useMemo(
    () => (client as any)?.userId ?? (client as any)?.id,
    [client]
  );

  // Fetch user
  const fetchUser = React.useCallback(
    async (token: string) => {
      const url = `${LAMBDA_BASE}?userId=${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Fetch user failed: ${res.status} ${text}`);
      }
      return (await res.json()) as User;
    },
    [userId]
  );

  // PUT update
  const putUserUpdate = React.useCallback(
    async (token: string, partial: Partial<User>) => {
      const url = `${LAMBDA_BASE}?userId=${encodeURIComponent(userId)}`;
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(partial),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`Update user failed: ${res.status} ${text}`);
      }
      return (await res.json()) as User;
    },
    [userId]
  );

  // Template handlers
  const handleRequestAITemplate = React.useCallback(
    async (notes: string) => {
      const res = await fetch("/api/ai-template", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
        },
        body: JSON.stringify({ notes, clientId: userId }),
      });
      if (!res.ok) throw new Error(`AI template failed: ${res.status}`);
      const tpl = (await res.json()) as TemplateType;
      return ensureTemplateIds(tpl);
    },
    [idToken, userId]
  );

  const handleSaveTemplate = React.useCallback(
    async (incoming: TemplateType) => {
      if (isFetchingToken) throw new Error("Auth token not ready yet.");
      if (!idToken) throw new Error("Missing ID token");

      const prepared = ensureTemplateIds(incoming);
      const latest = await fetchUser(idToken);

      const prevTemplates: TemplateType[] = Array.isArray(
        (latest as any).templates
      )
        ? ((latest as any).templates as TemplateType[])
        : [];

      const nextTemplates = upsertTemplate(prevTemplates, prepared);
      await putUserUpdate(idToken, {
        ...(latest as any),
        templates: nextTemplates,
      });
    },
    [fetchUser, putUserUpdate, idToken, isFetchingToken]
  );

  const handleDeleteTemplate = React.useCallback(
    async (templateId: string) => {
      if (isFetchingToken) throw new Error("Auth token not ready yet.");
      if (!idToken) throw new Error("Missing ID token");

      const latest = await fetchUser(idToken);
      const prevTemplates: TemplateType[] = Array.isArray(
        (latest as any).templates
      )
        ? ((latest as any).templates as TemplateType[])
        : [];

      const nextTemplates = prevTemplates.filter((t) => t.id !== templateId);
      await putUserUpdate(idToken, {
        ...(latest as any),
        templates: nextTemplates,
      });
    },
    [fetchUser, putUserUpdate, idToken, isFetchingToken]
  );

  return (
    <Box>
      <TemplateScroller
        templates={(client as any)?.templates ?? []}
        exercisesList={exercisesList}
        onRequestAITemplate={handleRequestAITemplate}
        onSaveTemplate={handleSaveTemplate}
        onDeleteTemplate={handleDeleteTemplate}
      />

      {/* 🆕 Charts reflect unified range; no local range controls */}
      <ExerciseDashboardWeb
        workouts={workouts}
        range={dateRange}
        showControls={false}
      />

      {/* Workout list */}
      <Box mt={2} sx={{ mt: 3, mb: 8, overflow: "visible" }}>
        {(!filtered || filtered.length === 0) && (
          <Typography color="text.primary">
            No workouts in this range.
          </Typography>
        )}

        <Stack spacing={1.25} sx={{}}>
          {filtered.map((workout, idx) => {
            const day = new Date(workout.dayOfExecution);
            const count = workout.template?.exercises?.length ?? 0;

            return (
              <GlassContainer>
                <Card
                  key={workout.id}
                  variant="outlined"
                  sx={(theme) => ({
                    borderRadius: 2,
                    backgroundColor: "transparent",
                    color: theme.palette.text.primary,
                    transition: theme.transitions.create("box-shadow", {
                      duration: 180,
                    }),
                    border: "0px solid transparent",
                    "&:hover": { boxShadow: 2 },
                  })}
                >
                  <CardActionArea
                    onClick={() => setSelected(workout)}
                    sx={{ px: 2, py: 1.5 }}
                  >
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      alignItems={{ xs: "flex-start", sm: "center" }}
                      spacing={1}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          mr: { sm: 1 },
                          lineHeight: 1.4,
                          color: "text.primary",
                        }}
                      >
                        {workout.template?.title ?? "Workout"}
                      </Typography>

                      <Box sx={{ flex: 1 }} />

                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<CalendarMonthIcon />}
                          label={formatDateLong(day)}
                          sx={{
                            color: "text.primary",
                            "& .MuiChip-label": { fontWeight: 600 },
                            "& .MuiChip-icon": {
                              fontSize: 18,
                              color: "text.secondary",
                            },
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                          }}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<AccessTimeIcon />}
                          label={`${workout.durationInMinutes ?? 0} min`}
                          sx={{
                            color: "text.primary",
                            "& .MuiChip-label": { fontWeight: 600 },
                            "& .MuiChip-icon": {
                              fontSize: 18,
                              color: "text.secondary",
                            },
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                          }}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<FitnessCenterIcon />}
                          label={(workout.totalVolume ?? 0).toLocaleString()}
                          sx={{
                            color: "text.primary",
                            "& .MuiChip-label": { fontWeight: 600 },
                            "& .MuiChip-icon": {
                              fontSize: 18,
                              color: "text.secondary",
                            },
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                          }}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          icon={<AssignmentTurnedInIcon />}
                          label={`${count} ${
                            count === 1 ? "exercise" : "exercises"
                          }`}
                          sx={{
                            color: "text.primary",
                            "& .MuiChip-label": { fontWeight: 600 },
                            "& .MuiChip-icon": {
                              fontSize: 18,
                              color: "text.secondary",
                            },
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                          }}
                        />
                      </Stack>
                    </Stack>
                  </CardActionArea>
                </Card>
              </GlassContainer>
            );
          })}
        </Stack>
      </Box>

      {/* Right slide-over for workout details */}
      <Drawer
        anchor="right"
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        PaperProps={{
          sx: (theme) => ({
            width: isLg ? 840 : 420,
            maxWidth: "100vw",
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          }),
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1,
            background: "background.paper",
            px: 2,
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 800, flex: 1, color: "text.primary" }}
            >
              {selected?.template?.title ?? "Workout"}
            </Typography>
            <IconButton
              aria-label="Close"
              size="small"
              onClick={() => setSelected(null)}
            >
              <CloseIcon />
            </IconButton>
          </Stack>

          {selected && (
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap">
              <Chip
                size="small"
                variant="outlined"
                icon={<CalendarMonthIcon />}
                label={formatDateLong(new Date(selected.dayOfExecution))}
                sx={{
                  color: "text.primary",
                  "& .MuiChip-label": { fontWeight: 600 },
                  "& .MuiChip-icon": { fontSize: 18, color: "text.secondary" },
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<AccessTimeIcon />}
                label={`${selected.durationInMinutes ?? 0} min`}
                sx={{
                  color: "text.primary",
                  "& .MuiChip-label": { fontWeight: 600 },
                  "& .MuiChip-icon": { fontSize: 18, color: "text.secondary" },
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<FitnessCenterIcon />}
                label={(selected.totalVolume ?? 0).toLocaleString()}
                sx={{
                  color: "text.primary",
                  "& .MuiChip-label": { fontWeight: 600 },
                  "& .MuiChip-icon": { fontSize: 18, color: "text.secondary" },
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              />
              <Chip
                size="small"
                variant="outlined"
                icon={<AssignmentTurnedInIcon />}
                label={`${exercises.length} ${
                  exercises.length === 1 ? "exercise" : "exercises"
                }`}
                sx={{
                  color: "text.primary",
                  "& .MuiChip-label": { fontWeight: 600 },
                  "& .MuiChip-icon": { fontSize: 18, color: "text.secondary" },
                  borderColor: "divider",
                  backgroundColor: "background.paper",
                }}
              />
            </Stack>
          )}
        </Box>

        {/* Body */}
        <Box sx={{ px: 2, py: 2, overflowY: "auto", color: "text.primary" }}>
          {!exercises.length && (
            <Typography variant="body2" color="text.secondary">
              No exercises recorded.
            </Typography>
          )}

          <Stack spacing={2} sx={{ pb: 6 }}>
            {exercises.map((exerciseTemplate: any, idx: number) => {
              const ex = exerciseTemplate.exercise;
              return (
                <Box
                  key={exerciseTemplate.id ?? idx}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 1.5,
                    backgroundColor: "background.paper",
                    color: "text.primary",
                  }}
                >
                  {/* Exercise header */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    sx={{ mb: 0.75 }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 800, color: "text.primary" }}
                    >
                      {ex?.name ?? "Exercise"}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {ex?.type && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Type: ${ex.type}`}
                          sx={{ color: "text.primary", borderColor: "divider" }}
                        />
                      )}
                      {ex?.muscleGroup && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`Group: ${ex.muscleGroup}`}
                          sx={{ color: "text.primary", borderColor: "divider" }}
                        />
                      )}
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<TimerIcon />}
                        label={`Rest ${
                          exerciseTemplate.restTimer?.valueInSeconds ?? 0
                        }s`}
                        sx={{
                          color: "text.primary",
                          borderColor: "divider",
                          "& .MuiChip-icon": { color: "text.secondary" },
                        }}
                      />
                    </Stack>
                  </Stack>

                  {/* Sets */}
                  <Box
                    sx={{
                      borderRadius: 1.5,
                      overflow: "hidden",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    {(exerciseTemplate.sets ?? []).map(
                      (set: any, i: number) => (
                        <Stack
                          key={set.id ?? i}
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={(theme) => ({
                            px: 1.25,
                            py: 0.75,
                            background:
                              i % 2 === 0
                                ? "transparent"
                                : alpha(theme.palette.action.hover, 0.12),
                          })}
                        >
                          <Box
                            sx={(theme) => ({
                              width: 22,
                              height: 22,
                              display: "grid",
                              placeItems: "center",
                              borderRadius: 1,
                              fontSize: 12,
                              fontWeight: 700,
                              mr: 0.5,
                              background: alpha(
                                theme.palette.text.primary,
                                0.06
                              ),
                              color: theme.palette.text.secondary,
                            })}
                          >
                            {i + 1}
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              minWidth: 64,
                              fontWeight: 600,
                              color: "text.primary",
                            }}
                          >
                            Set {i + 1}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            Previous — {set.previous}
                          </Typography>

                          <Box
                            sx={{
                              mx: 1,
                              height: 24,
                              borderLeft: "1px solid",
                              borderColor: "divider",
                            }}
                          />

                          <Typography
                            variant="body2"
                            sx={{ color: "text.primary" }}
                          >
                            Current —{" "}
                            <b>
                              {set.current?.weight}
                              {isMetricUnits ? "kg" : "lbs"} ×{" "}
                              {set.current?.reps}
                            </b>
                          </Typography>
                        </Stack>
                      )
                    )}
                  </Box>
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Drawer>
    </Box>
  );
};

export default ExerciseList;
